// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import type { WorkItemStatus } from '@web/lib/remoteWorkItemModel';

export const ACTIVE_WORK_STATUSES = new Set<WorkItemStatus>([
  'queued',
  'in_progress',
  'waiting',
]);

export const WORK_STATUS_LABEL: Record<WorkItemStatus, string> = {
  queued: 'Queued',
  in_progress: 'Running',
  waiting: 'Waiting for you',
  completed: 'Completed',
  failed: 'Failed',
  interrupted: 'Interrupted',
};

export const WORK_STATUS_CLASS: Record<WorkItemStatus, string> = {
  queued:
    'bg-ds-bg-status-pending-subtle-default text-ds-text-status-pending-strong-default',
  in_progress:
    'bg-ds-bg-status-running-subtle-default text-ds-text-status-running-strong-default',
  waiting:
    'bg-ds-bg-status-pending-subtle-default text-ds-text-status-pending-strong-default',
  completed:
    'bg-ds-bg-status-completed-subtle-default text-ds-text-status-completed-strong-default',
  failed:
    'bg-ds-bg-status-error-subtle-default text-ds-text-status-error-strong-default',
  interrupted:
    'bg-ds-bg-status-paused-subtle-default text-ds-text-status-paused-strong-default',
};

export function isActiveWorkStatus(status: WorkItemStatus): boolean {
  return ACTIVE_WORK_STATUSES.has(status);
}

export function formatDuration(durationMs: number | null | undefined): string {
  if (durationMs == null || !Number.isFinite(durationMs) || durationMs < 0) {
    return '—';
  }

  const seconds = Math.max(0, Math.floor(durationMs / 1000));
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds === 0
      ? `${minutes}m`
      : `${minutes}m ${remainingSeconds}s`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0
    ? `${hours}h`
    : `${hours}h ${remainingMinutes}m`;
}

export function durationToDateTime(
  durationMs: number | null | undefined
): string | undefined {
  if (durationMs == null || !Number.isFinite(durationMs) || durationMs < 0) {
    return undefined;
  }
  return `PT${Math.max(0, Math.floor(durationMs / 1000))}S`;
}

function parseTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

export function resolveDurationMs({
  status,
  startedAt,
  completedAt,
  durationMs,
  now,
}: {
  status: WorkItemStatus;
  startedAt?: string | null;
  completedAt?: string | null;
  durationMs?: number | null;
  now: number;
}): number | null {
  const start = parseTimestamp(startedAt);
  const end = parseTimestamp(completedAt);

  if (start != null) {
    if (end != null) return Math.max(0, end - start);
    if (isActiveWorkStatus(status)) return Math.max(0, now - start);
  }

  return durationMs != null && Number.isFinite(durationMs)
    ? Math.max(0, durationMs)
    : null;
}

export function fallbackActivityText(status: WorkItemStatus): string {
  switch (status) {
    case 'queued':
      return 'Waiting to start…';
    case 'in_progress':
      return 'Working…';
    case 'waiting':
      return 'Waiting for your input.';
    case 'completed':
      return 'Work completed.';
    case 'failed':
      return 'Agent work failed.';
    case 'interrupted':
      return 'Agent work was interrupted.';
  }
}
